<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import AppDataTable from "@template/components/AppDataTable.vue";
import Button from "@/components/Base/Button";
import Lucide from "@/components/Base/Lucide";
import { useI18n } from "vue-i18n";
import api from "@/utils/axios";
import { useRouter } from "vue-router";
import dayjs from "dayjs";
import LoadingIcon from "@/components/Base/LoadingIcon";
import type { AppDataTableColumn } from "@template/components/AppDataTable.vue";
import { nestedName } from "@template/utils/appDataTableCellValue";
import PageTitle from "@template/components/PageTitle/PageTitle.vue";

const { t } = useI18n();
const router = useRouter();

const loans = ref<any[]>([]);
const isLoading = ref(true);

// Columnas simplificadas para el usuario
const tableColumns = computed((): AppDataTableColumn[] => [
  {
    key: "consultation_date",
    label: t("loans.columns.consultation_date"),
    sortValue: (r) => String(r.consultation_date ?? ""),
  },
  { key: "shift", label: t("loans.columns.shift"), sortValue: (r) => String(r.shift ?? "") },
  {
    key: "reason",
    label: t("loans.columns.reason"),
    sortValue: (r) => nestedName(r as Parameters<typeof nestedName>[0], "reason"),
  },
  {
    key: "items_count",
    label: t("loans.columns.docs"),
    sortValue: (r) => Number(r.items_count) || 0,
  },
  { key: "status", label: t("loans.columns.status"), sortValue: (r) => String(r.status ?? "") },
  { key: "actions", label: t("loans.columns.actions"), sortable: false, filterable: false },
]);

// Traer solo los pendientes (solicitados y activos)
const fetchPendingLoans = async () => {
  isLoading.value = true;
  try {
    const res = await api.get("/loans", {
      params: {
        page: 1,
        size: 50,
        status: "requested,active",
      },
    });

    const payload = res.data ?? {};
    loans.value = payload.data ?? [];
  } catch (e) {
    console.error("Error fetching user pending loans", e);
    loans.value = [];
  } finally {
    isLoading.value = false;
  }
};

const refresh = () => {
  fetchPendingLoans();
};

defineExpose({ refresh });

onMounted(() => {
  fetchPendingLoans();
});
</script>

<template>
  <div class="col-span-12">
    <div class="intro-y flex flex-col gap-4 sm:flex-row sm:items-center mb-5">
      <PageTitle
        :title="$t('dashboard.pending_loans', 'Préstamos Pendientes')"
        icon="BookKey"
        variant="emerald"
        heading="h3"
      />
      <div class="flex items-center sm:ml-auto">
        <Button variant="outline-primary" class="mr-2 flex items-center" @click="fetchPendingLoans">
          <Lucide icon="RefreshCw" class="w-4 h-4 mr-2" /> {{ $t('dashboard.update') }}
        </Button>
        <Button variant="primary" class="mr-2 flex items-center" @click="router.push('/loans/create?from=dashboard')">
            <Lucide icon="Plus" class="w-4 h-4 mr-2" /> {{ $t('loans.new_request') }}
        </Button>
      </div>
    </div>
    
    <div class="intro-y box p-5 relative">
      <div v-if="isLoading" class="absolute inset-0 bg-white/80 z-50 flex items-center justify-center min-h-[200px]">
        <div class="w-16 h-16">
          <LoadingIcon icon="bars" class="w-full h-full" color="#1e293b" />
        </div>
      </div>
      <div>
        <AppDataTable
          :columns="tableColumns"
          :data="loans"
          :enable-search="false"
          :disable-client-search="true"
        >
          <template #cell-consultation_date="{ item }">
            {{
              item?.consultation_date
                ? dayjs(item.consultation_date).format("DD/MM/YYYY")
                : "-"
            }}
          </template>

          <template #cell-shift="{ item }">
            {{
              item?.shift === "morning"
                ? $t('loans.shifts.morning')
                : item?.shift === "afternoon"
                  ? $t('loans.shifts.afternoon')
                  : $t('loans.shifts.full_day')
            }}
          </template>

          <template #cell-reason="{ item }">
            {{ item?.reason?.name ?? "-" }}
          </template>

          <template #cell-items_count="{ item }">
            <span class="bg-slate-100 text-slate-600 rounded-full px-2 py-1 text-xs font-bold whitespace-nowrap">
               {{ item?.items_count ?? 0 }} {{ $t('loans.columns.docs').toLowerCase() }}
             </span>
          </template>

          <template #cell-status="{ item }">
            <span
              class="text-xs px-2 py-1 rounded"
              :class="{
                'bg-primary text-white': item?.status === 'active',
                'bg-warning text-slate-800': item?.status === 'requested',
                'bg-success text-white': item?.status === 'returned',
                'bg-danger text-white': item?.status === 'overdue',
                'bg-slate-200 text-slate-700': ![
                  'active',
                  'requested',
                  'returned',
                  'overdue'
                ].includes(item?.status)
              }"
            >
              {{
                item?.status === "active"
                  ? $t('loans.status.active')
                  : item?.status === "requested"
                    ? $t('loans.status.requested')
                    : item?.status === "returned"
                      ? $t('loans.status.returned')
                      : item?.status === "overdue"
                        ? $t('loans.status.overdue')
                        : item?.status ?? "-"
              }}
            </span>
          </template>

          <template #cell-actions="{ item }">
            <div class="flex items-center justify-center gap-2">
              <button
                type="button"
                class="border border-slate-200 rounded p-1 hover:bg-slate-100"
                @click="router.push(`/loans/${item.id}?from=dashboard`)"
                :title="$t('documents.actions.view')"
              >
                <Lucide icon="Eye" class="stroke-[1.3] w-4 h-4" />
              </button>
            </div>
          </template>
        </AppDataTable>
      </div>
    </div>
  </div>
</template>
